
// Utils

import { normalizeNameOfPerson } from '../normalize/normalizeNameOfPerson';
import { getHhmByPersonId } from '../normalize/getHhmByPersonId';
import { householdMemberTools } from '../tools/householdMemberTools';
import { preventerTools } from '../tools/preventerTools';
import { isEqual, omit } from '../miscellaneous/objectHelpers';
import { getDateDifference } from '../dates/date.utils';

// Constants

import { THC_TYPES } from '@constants/ssap/hhmTypes/taxRelationsTypes';
import { BLOOD_RELATIONSHIP_CODES, BLOOD_RELATIONSHIP_TYPES } from '@constants/ssap/hhmTypes/bloodRelationshipCodesList';

export const HHM_ATTRIBUTES = {
    ADULT_WITH_PARTNER: 'ADULT_WITH_PARTNER',
    ADULT_WITHOUT_PARTNER_WITH_KIDS: 'ADULT_WITHOUT_PARTNER_WITH_KIDS',
    ADULT_WITHOUT_PARTNER_WITHOUT_KIDS: 'ADULT_WITHOUT_PARTNER_WITHOUT_KIDS',
    CHILD: 'CHILD',
    LIVE_SEPARATELY: 'LIVE_SEPARATELY',
    LIVE_TOGETHER: 'LIVE_TOGETHER'
};

export const analyzeTaxHouseholdComposition = params => {

    const {
        householdMembers,
        relations,
        bloodRelationship,
        serverDate,
        primaryTaxFilerPersonId,
        isState
    } = params;

    const {
        codes,
        loadedRelations
    } = relations;

    analyzeTaxHouseholdComposition.getHhmByPersonId = getHhmByPersonId;

    analyzeTaxHouseholdComposition.checkIfLiveTogether = ids => {
        const addressIds = householdMembers
            .filter(hhm => ids.some(id => id === hhm.personId))
            .map(hhm => hhm.addressId);
        return [...new Set(addressIds)].length === 1;
    };

    analyzeTaxHouseholdComposition.updateDependentHouseholdId = (dependent, filerPersonId, isMemberChecked) => {

        if(!isMemberChecked) {
            return {
                ...dependent,
                householdId: 0
            };
        }

        const index = Array.isArray(filerPersonId) ? filerPersonId[0] - 1 : filerPersonId - 1;

        return {
            ...dependent,
            householdId: householdMembers[index].householdId
        };
    };

    analyzeTaxHouseholdComposition.updateHhmTopProp = (hhm, objForMerge) => {
        return {
            ...hhm,
            ...objForMerge
        };
    };

    analyzeTaxHouseholdComposition.updateHhm = (hhm, objForMerge) => {
        return {
            ...hhm,
            taxHouseholdComposition: {
                ...hhm.taxHouseholdComposition,
                ...objForMerge
            }
        };
    };

    analyzeTaxHouseholdComposition.getRelationByIds = (...ids) => {
        const [ individualPersonId, relatedPersonId ] = ids;

        return bloodRelationship.find(br => {
            if(Number(br.individualPersonId) === individualPersonId && Number(br.relatedPersonId) === relatedPersonId) {
                return br;
            }
        });
    };

    analyzeTaxHouseholdComposition.hasDependents = personId => {
        const claimers = householdMembers.reduce((claimers, hhm) => claimers.concat(hhm.taxHouseholdComposition.claimerIds), []);
        return claimers.find(claimer => claimer === personId);
    };

    const getHhmIndex = personId => householdMembers.findIndex(hhm => hhm.personId === Number(personId));
    const getHhmHouseholdId = personId => householdMembers.find(hhm => hhm.personId === Number(personId)).householdId;

    analyzeTaxHouseholdComposition.getIndicatorsValues = (personId, anyHhm = undefined) => {

        const index = getHhmIndex(personId);

        const {
            taxHouseholdComposition
        } = anyHhm ? anyHhm: householdMembers[index];

        return omit(taxHouseholdComposition, ['claimerIds']);
    };

    analyzeTaxHouseholdComposition.isCountedAsPair = ids => {
        return ids.every(id => getDateDifference(serverDate, analyzeTaxHouseholdComposition.getHhmByPersonId(Number(id), householdMembers).dateOfBirth, 'years') >= 14);
    };

    const doSpousesFileJointly = ids => {
        return ids
            .map(id => analyzeTaxHouseholdComposition.getIndicatorsValues(id).taxFilingStatus)
            .every(status => status === THC_TYPES.FILING_JOINTLY);
    };

    const doSpousesFileSeparately = ids => {
        return ids
            .map(id => analyzeTaxHouseholdComposition.getIndicatorsValues(id).taxFilingStatus)
            .some(status => status === THC_TYPES.FILING_SEPARATELY);
    };

    const hhmData = (dataKey, personId) => {
        const hhmIndex = getHhmIndex(personId);
        return householdMembers[hhmIndex][dataKey];
    };

    const doLiveTogether = ids => {
        const filtered = householdMembers
            .filter(hhm => ids.some(id => Number(id) === hhm.personId))
            .map(hhm => hhm.addressId);

        return filtered.every(hhm => hhm === filtered[0]);
    };

    const filers = (hhms => hhms.map(hhm => {

        const {
            dateOfBirth,
            name,
            personId
        } = hhm;

        const {
            taxRelationship,
            taxFilingStatus
        } = analyzeTaxHouseholdComposition.getIndicatorsValues(personId);

        const filerAge = getDateDifference(serverDate, dateOfBirth, 'years');
        const isFilingTaxes = taxRelationship === THC_TYPES.FILER || taxRelationship === THC_TYPES.FILER_DEPENDENT;

        return {
            filerAge: filerAge,
            filerIndex: getHhmIndex(personId),
            filerHouseholdId: getHhmHouseholdId(personId),
            filerPersonId: personId,
            filerFullName: normalizeNameOfPerson(name),
            isFilingTaxes: isFilingTaxes,
            taxFilingStatus: taxFilingStatus,
            isPureFiler: taxRelationship === THC_TYPES.FILER
        };
    }))(householdMembers);



    const filersQty = filers.reduce((acc, el) => acc + (el.isFilingTaxes ? 1 : 0), 0);

    const pairs = (() => {
        const spouses = new Map();

        const checkIfAnyOfSpousesFiler = ids =>
            ids.some(id => filers.some(filer => filer.filerPersonId === id && filer.isFilingTaxes));

        bloodRelationship.forEach(br => {

            const {
                individualPersonId,
                relatedPersonId,
                relation,
                personGuid,
                relatedPersonGuid
            } = br;

            const bothMembers = [individualPersonId, relatedPersonId];

            if(relation === codes.SPOUSE) {
                if(!spouses.has(bothMembers.sort().toString())) {

                    spouses.set(bothMembers.sort().toString(),  {
                        pairId: bothMembers.toString(),
                        isPairAccordingToLaw: analyzeTaxHouseholdComposition.isCountedAsPair(bothMembers),
                        spouseIndexes: bothMembers.map(m => getHhmIndex(m)),
                        spousesPersonIds: [Number(individualPersonId), Number(relatedPersonId)],
                        spouse1: {
                            spouse1gender: householdMemberTools.extractFromHouseholdMembers(householdMembers).getHhmGenderByGuid(personGuid),
                            spouse1personId: individualPersonId,
                            spouse1index: getHhmIndex(individualPersonId),
                            spouse1fullName: normalizeNameOfPerson(hhmData('name', individualPersonId)),

                        },
                        spouse2: {
                            spouse2gender: householdMemberTools.extractFromHouseholdMembers(householdMembers).getHhmGenderByGuid(relatedPersonGuid),
                            spouse2personId: relatedPersonId,
                            spouse2index: getHhmIndex(relatedPersonId),
                            spouse2fullName: normalizeNameOfPerson(hhmData('name', relatedPersonId))
                        },
                        liveTogether: doLiveTogether(bothMembers),
                        doSpousesFileTogether: doSpousesFileJointly(bothMembers),
                        doSpousesFileSeparately: doSpousesFileSeparately(bothMembers),
                        isPairMadeChoice: doSpousesFileJointly(bothMembers) || doSpousesFileSeparately(bothMembers),
                        isAnyOfSpousesFiler: checkIfAnyOfSpousesFiler([Number(individualPersonId), Number(relatedPersonId)])
                    });
                }
            }
        });
        return [...spouses].map(([, pairData]) => pairData);
    })();

    analyzeTaxHouseholdComposition.getSpouseIndex = idx => {
        return (pair => pair.spouseIndexes.filter(spIndex => spIndex !== idx)[0])(pairs.filter(pair => pair.spouseIndexes.some(index => index === idx))[0]);
    };

    const combinedFilers = (filers => {
        const output = [];
        pairs.forEach(pair => {
            if(pair.doSpousesFileTogether) {
                output.push({
                    filerIndex: pair.spouseIndexes,
                    filerPersonId: pair.spousesPersonIds,
                    filerFullName: [pair.spouse1.spouse1fullName, pair.spouse2.spouse2fullName],
                    isFilingTaxes: true,
                    taxFilingStatus: THC_TYPES.FILING_JOINTLY
                });
            }
        });
        filers.forEach(filer => {
            const checkIfNotInOutput = () => {
                return output.every(f => {
                    if(Array.isArray(f.filerIndex)) {
                        return f.filerIndex.every(f => f !== filer.filerIndex);
                    } else {
                        return f.filerIndex !== filer.filerIndex;
                    }
                });
            };
            if(checkIfNotInOutput()) {
                output.push(filer);
            }
        });
        return output;
    })(filers);

    const filteredCombinedFilers = combinedFilers.filter(f => f.isFilingTaxes);

    const bloodRelationshipsCodes = (() => {
        let output = {};
        const RELATIONS = [
            {
                type: 'spouse',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.SPOUSE
                ]
            },
            {
                type: 'biological_parents',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.PARENT,
                    BLOOD_RELATIONSHIP_TYPES.PARENT_OF_CHILD
                ]
            },
            {
                type: 'parents',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.PARENT,
                    BLOOD_RELATIONSHIP_TYPES.PARENT_OF_CHILD,
                    BLOOD_RELATIONSHIP_TYPES.STEPPARENT
                ]
            },
            {
                type: 'parents_or_guardians',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.PARENT,
                    BLOOD_RELATIONSHIP_TYPES.PARENT_OF_CHILD,
                    BLOOD_RELATIONSHIP_TYPES.STEPPARENT,
                    BLOOD_RELATIONSHIP_TYPES.FOSTER_PARENT,
                    BLOOD_RELATIONSHIP_TYPES.COURT_APPOINTED_GUARDIAN,
                    BLOOD_RELATIONSHIP_TYPES.GRANDPARENT,
                    BLOOD_RELATIONSHIP_TYPES.UNCLE_AUNT
                ]
            },
            {
                type: 'partners',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.SPOUSE,
                    BLOOD_RELATIONSHIP_TYPES.DOMESTIC_PARTNER,
                    BLOOD_RELATIONSHIP_TYPES.FORMER_SPOUSE
                ]
            },
            {
                type: 'siblings',
                enums: [
                    BLOOD_RELATIONSHIP_TYPES.SIBLING
                ]
            }
        ];
        loadedRelations.forEach(lr => {
            RELATIONS.forEach(r => {
                const {
                    type,
                    enums
                } = r;
                if(enums.some(e => e === lr.type)) {
                    if(output[type]) {
                        output = {
                            ...output,
                            [type]: [
                                ...output[type],
                                lr.code
                            ]
                        };
                    } else {
                        output = {
                            ...output,
                            [type]: [
                                lr.code
                            ]
                        };
                    }
                }

            });
        });
        return output;
    })();

    const getRelatedPersonsIds = (personId => {
        let output = {};
        Object.entries(bloodRelationshipsCodes).forEach(([ codeType, values ]) => {
            let res = [];
            bloodRelationship.forEach(br => {
                if(br.relatedPersonId === String(personId) && values.some(v => v === br.relation)) {
                    res.push(+br.individualPersonId);
                }
            });

            if(res.length > 0) {
                output = {
                    ...output,
                    [codeType]: res
                };
            } else {
                output = {
                    ...output,
                    [codeType]: null
                };
            }
        });
        return output;
    });

    const dependents = (hhms => {
        const dependents = [];
        hhms.forEach(hhm => {

            const {
                dateOfBirth,
                name,
                personId,
                addressId,
                taxHouseholdComposition: {
                    claimerIds,
                    taxRelationship
                }
            } = hhm;

            const isDependent = taxRelationship === THC_TYPES.DEPENDENT || taxRelationship === THC_TYPES.FILER_DEPENDENT;

            const isAbleToBeDependent = claimerIds.length === 0 &&
                hhms
                    .filter(hhm => hhm.personId !== personId)
                    .every(hhm => {
                        const {
                            taxHouseholdComposition: {
                                claimerIds
                            }
                        } = hhm;

                        return !claimerIds.some(id => id === personId);

                    });

            const isChild = (() => {
                return getDateDifference(serverDate, dateOfBirth, 'years') < 19;
            })();

            const relatedPersonsIds = getRelatedPersonsIds(personId);

            const isChildClaimedBySeparatelyLivingParent = (() => {
                return isChild
                    && claimerIds.length === 1
                    && analyzeTaxHouseholdComposition.getRelationByIds(personId, claimerIds[0]).relation === '19'
                    && !analyzeTaxHouseholdComposition.checkIfLiveTogether([personId, claimerIds[0]]);
            })();

            const isClaimedByITF = (() => {
                return claimerIds.some(id => {
                    const {
                        taxHouseholdComposition: {
                            independentTaxFiler
                        }
                    } = getHhmByPersonId(id, hhms);
                    return independentTaxFiler;
                });
            })();

            dependents.push({
                addressId,
                claimerIds,
                dependentIndex: getHhmIndex(personId),
                dependentFullName: normalizeNameOfPerson(name),
                dependentPersonId: personId,
                isAbleToBeDependent,
                isChild,
                isChildClaimedBySeparatelyLivingParent,
                isDependent,
                isClaimedByITF,
                parents: relatedPersonsIds.parents,
                parentsOrGuardians: relatedPersonsIds.parents_or_guardians,
                partners: relatedPersonsIds.partners,
                siblings: relatedPersonsIds.siblings,
                spouse: relatedPersonsIds.spouse
            });
        });
        return dependents;
    })(householdMembers);

    const dependentsQty = dependents.filter(dependent => dependent.isDependent).length;

    const possibleDependents = (() => {
        let counter = 0;
        filers.forEach(filer => {
            const {
                filerPersonId
            } = filer;

            const checkDependent = dependent => {
                const {
                    dependentPersonId
                } = dependent;
                if(Array.isArray(filerPersonId)) {
                    return filerPersonId.every(id => id !== dependentPersonId);
                } else {
                    return dependentPersonId !== filerPersonId;
                }
            };

            const possibleDependentsQty = dependents.filter(dependent => checkDependent(dependent)).length;

            if( possibleDependentsQty > 0) {
                counter += possibleDependentsQty;
            }
        });
        return counter;
    })();

    const independentTaxFilers = (hhms => {
        const independentTaxFilers = [];

        const isRequiredRelationshipToPTF = (personId, primaryTaxFilerPersonId) => {
            const ITFtoPTFRelation = preventerTools.provideObject(
                bloodRelationship
                    .find(br => +br.individualPersonId === personId && +br.relatedPersonId === primaryTaxFilerPersonId)
            );
            return Object.values(codes.itfConsidered).some(code => code === ITFtoPTFRelation.relation);
        };

        hhms.forEach((hhm, hhmIndex)  => {
            const { dateOfBirth, personId } = hhm;
            const ageInYears = getDateDifference(serverDate, dateOfBirth, 'years');
            if(
                ageInYears < 26
                && filers[hhmIndex].isPureFiler
                && isRequiredRelationshipToPTF(personId, primaryTaxFilerPersonId)
            ) {
                independentTaxFilers.push(hhmIndex);
            }
        });

        return independentTaxFilers;
    })(householdMembers);

    const allHhmAttributes = (hhms => {

        const ADULT_CHILD_THRESHOLD_FOR_TH_SUMMARY = isState.NM ? 25 : 18;

        const common = hhms.reduce(
            (common, hhm) => {
                const {
                    dateOfBirth,
                    livesAtOtherAddressIndicator
                } = hhm;
                const age = getDateDifference(serverDate, dateOfBirth, 'years');

                let output = common;

                if(age < ADULT_CHILD_THRESHOLD_FOR_TH_SUMMARY) { // is a child
                    output = {
                        ...output,
                        children: output.children + 1
                    };
                }

                if(output.livingArrangement !== HHM_ATTRIBUTES.LIVE_SEPARATELY) {
                    output = {
                        ...output,
                        livingArrangement: livesAtOtherAddressIndicator
                            ? HHM_ATTRIBUTES.LIVE_SEPARATELY
                            : HHM_ATTRIBUTES.LIVE_TOGETHER
                    };
                }

                return output;
            },
            {
                children: 0,
                livingArrangement: HHM_ATTRIBUTES.LIVE_TOGETHER
            }
        );

        return hhms.reduce(
            (output, hhm, idx) => {
                const {
                    dateOfBirth,
                    name,
                    personId
                } = hhm;

                const personName = normalizeNameOfPerson(name);

                const isAdult = (
                    age => age >= ADULT_CHILD_THRESHOLD_FOR_TH_SUMMARY
                )(getDateDifference(serverDate, dateOfBirth, 'years'));

                const parentsOrGuardians = dependents[idx].parentsOrGuardians?.length || 0;
                const siblings = dependents[idx].siblings?.length || 0;

                const [ partnerId ] = dependents[idx].partners ?? [];

                const partnerRelationshipCode = partnerId
                    ? analyzeTaxHouseholdComposition.getRelationByIds(personId, partnerId).relation
                    : null;

                const partnerRelationType = relations
                    .dropdownOptions
                    ?.find(relation => relation.code === partnerRelationshipCode)
                    ?.type ?? '';

                const partnerRelationshipTextId = `ssap.input.option.${ partnerRelationType.toLowerCase() }`;

                let status;

                switch(true) {
                    case isAdult && !!partnerId:
                        status = HHM_ATTRIBUTES.ADULT_WITH_PARTNER;
                        break;
                    case isAdult && !partnerId && output.common.children > 0:
                        status = HHM_ATTRIBUTES.ADULT_WITHOUT_PARTNER_WITH_KIDS;
                        break;
                    case isAdult && !partnerId && output.common.children === 0:
                        status = HHM_ATTRIBUTES.ADULT_WITHOUT_PARTNER_WITHOUT_KIDS;
                        break;
                    default:
                        status = HHM_ATTRIBUTES.CHILD;
                        break;
                }

                return {
                    ...output,
                    individual: [
                        ...output.individual,
                        {
                            parentsOrGuardians,
                            partnerRelationshipTextId,
                            personName,
                            status,
                            siblings
                        }
                    ]
                };
            },
            {
                common,
                individual: []

            }
        );
    })(householdMembers);

    const outsideClaimers = ((hhms, filers) => {
        const result = [];

        hhms.forEach((hhm, i) => {
            const {
                personId
            } = hhm;

            const {
                claimerIds = [],
                taxFilingStatus
            } = hhm.taxHouseholdComposition;

            const isNotFilingJointly = taxFilingStatus !== THC_TYPES.FILING_JOINTLY;

            const isNotClaimingTaxDependent = !analyzeTaxHouseholdComposition.hasDependents(personId);

            const isNotClaimedByHouseholdFiler = !claimerIds.some(claimerPersonId =>
                filers.some(f =>
                    Number(f.filerPersonId) === Number(claimerPersonId) && f.isFilingTaxes
                )
            );

            // Updated condition:
            // If person is or is NOT a tax filer
            // is NOT filing jointly
            // AND is NOT claiming a tax dependent
            // AND person is ANY age
            // AND no filers in the household are claiming the person as a tax dependent.
            if(isNotFilingJointly && isNotClaimingTaxDependent && isNotClaimedByHouseholdFiler) {
                result.push({
                    filerAge: filers[i].filerAge,
                    filerFullName: filers[i].filerFullName,
                    indexInHhmsArray: i
                });
            }
        });

        return result;
    })(householdMembers, filers);

    return {
        allHhmAttributes,
        combinedFilers,
        dependents,
        dependentsQty,
        filersQty,
        filers,
        filteredCombinedFilers,
        pairs,
        possibleDependents,
        independentTaxFilers,
        outsideClaimers
    };
};

const isMarriedToPerson = (personId, relatedPersonId, bloodRelationship) =>
    bloodRelationship.some(({ individualPersonId, relatedPersonId: relatedId, relation }) =>
        relation === BLOOD_RELATIONSHIP_CODES.SPOUSE &&
        (
            (Number(individualPersonId) === Number(personId) && Number(relatedId) === Number(relatedPersonId)) ||
            (Number(relatedId) === Number(personId) && Number(individualPersonId) === Number(relatedPersonId))
        )
    );

export const checkIfMultiTaxHousehold = params => {
    const  {
        combinedFilers,
        filers,
        isState,
        primaryTaxFilerPersonId,
        bloodRelationship = []
    } = params;

    const filerPersonIds = combinedFilers.reduce(
        (filerPersonIds, filer) => {
            const {
                filerPersonId,
                taxFilingStatus
            } = filer;

            return (Array.isArray(filerPersonId) && taxFilingStatus === THC_TYPES.FILING_JOINTLY)
                ? [ ...filerPersonIds, filerPersonId.sort() ]
                : filerPersonIds;
        },
        [ ]
    );

    const filterEligibleForRenderFiler = f => {
        const {
            isFilingTaxes,
            filerPersonId,
            taxFilingStatus,
            isPureFiler
        } = f;

        if (isState.MN) {
            const isNotMarriedToPrimaryTaxFiler = !isMarriedToPerson(
                filerPersonId,
                primaryTaxFilerPersonId,
                bloodRelationship
            );

            // Suppress the modal IF the non-primary filers are not married to the primary filer
            // AND the non-primary filer is being claimed as a tax dependent by the primary tax household.
            if (isNotMarriedToPrimaryTaxFiler && isPureFiler === false) {
                return false;
            }
        }

        if(taxFilingStatus === THC_TYPES.FILING_SEPARATELY) {
            return false;
        }

        const ids = [ filerPersonId, primaryTaxFilerPersonId ].sort();
        const isFilingJointlyWithPtf = filerPersonIds.some(filerPersonId => isEqual(ids, filerPersonId));

        return filerPersonId !== primaryTaxFilerPersonId && isFilingTaxes && !isFilingJointlyWithPtf;
    };

    const filteredFilers = filers.filter(filterEligibleForRenderFiler);

    return {
        isMultiTaxHousehold: filteredFilers.length > 0,
        filteredFilers
    };
};